import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Heading, Spinner, Text } from '@radix-ui/themes'
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useTRPC } from '~/utils/trpc'
import { StationLeakV0Panel } from '../leak-v0/StationLeakV0Panel'
import { summarizeLeakV0Status } from '../leak-v0/summary'
import type { StationSectionProps } from '../types'

export const StationLeakDetectionCard = ({ station }: StationSectionProps) => {
  const trpc = useTRPC()
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    data: status,
    isLoading,
    error
  } = useQuery(
    // An errored query holds no data, so `staleTime` cannot mark it fresh and every background
    // trigger would re-run the procedure's unindexed last-reading scan. Remounting still retries.
    trpc.ops.stations.leakV0Status.queryOptions(
      { stationId: station.id },
      { staleTime: Infinity, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false }
    )
  )

  const summary = status ? summarizeLeakV0Status(status) : null

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Heading as="h2" size="3">
          <Flex asChild align="center" gap="2" wrap="wrap" width="100%">
            <button
              type="button"
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((expanded) => !expanded)}
              className="cursor-pointer text-left"
            >
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              Leak detection
              {isLoading ? <Spinner size="2" /> : null}
              {error ? (
                <Text size="2" weight="regular" color="red">
                  Status unavailable · {error.message}
                </Text>
              ) : null}
              {summary ? (
                <Text size="2" weight="regular" color={summary.color}>
                  {summary.facts.join(' · ')}
                </Text>
              ) : null}
            </button>
          </Flex>
        </Heading>

        {isExpanded && status ? <StationLeakV0Panel station={station} status={status} /> : null}
      </Flex>
    </Card>
  )
}
